import {
  useApiClient,
  WebshopFavoritesCommands,
  WebshopFavoritesEvent,
  WebshopFavoritesProduct,
  WebshopFavoritesQueries,
  WebshopFavoritesVersioned,
  webshopFavoritesAddProductService,
} from '@cldsystems/ws-api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { FavoritesActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { UpdateMutateProps } from '@/types/common';
import { isNoChangesError, isOptimisticLockError } from '@/utils/get-api-error-message';

type FavoritesAddProductMutationPayload = {
  productId: string;
  event: WebshopFavoritesEvent;
  product: WebshopFavoritesProduct;
  operationId: string;
  buttonType?: FavoritesActionButtonsEnum;
  optimisticApplied?: boolean;
  productWasInFavorites: boolean;
  /** Skip the error toast for a (retryable) optimistic-lock conflict. */
  suppressVersionConflictToast?: boolean;
};

type FavoritesAddProductMutationContext = {
  favoritesId: string;
  productId: string;
  product: WebshopFavoritesProduct;
  operationId: string;
  productWasInFavorites: boolean;
  previousVersion?: number;
};

const useFavoritesAddProductMutate = (
  markFavoritePending: (
    productId: string,
    requestedAction: 'add' | 'remove',
    buttonType: FavoritesActionButtonsEnum | undefined,
    operationId: string,
  ) => void,
  clearFavoritePending: (productId: string, operationId?: string) => void,
  getFavoritePending: (productId: string) =>
    | {
        operationId: string;
      }
    | undefined,
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { hasSchema, httpClient } = useApiClient();

  return useMutation({
    mutationKey: [WebshopFavoritesCommands.AddProduct],
    mutationFn: ({ params, payload }: UpdateMutateProps<FavoritesAddProductMutationPayload>) =>
      webshopFavoritesAddProductService({
        httpClient,
        hasSchema: Boolean(hasSchema),
      })({ params, payload: { productIds: [payload!.productId], event: payload!.event } }),
    onMutate: async ({ params, payload }) => {
      if (!payload) return;

      markFavoritePending(payload.productId, 'add', payload.buttonType, payload.operationId);

      const queryKey = [WebshopFavoritesQueries.GetById, params.id];
      await queryClient.cancelQueries({ queryKey });
      const previousFavorites = queryClient.getQueryData<WebshopFavoritesVersioned>(queryKey);

      queryClient.setQueryData<WebshopFavoritesVersioned>(queryKey, old => {
        if (!old) return undefined;

        const isProductExists = !!old.favorites.products.find(p => p.id === payload.productId);
        const products = payload.optimisticApplied
          ? old.favorites.products
          : isProductExists
            ? old.favorites.products
            : [...old.favorites.products, payload.product];

        return {
          ...old,
          version: old.version + 1,
          favorites: {
            ...old.favorites,
            products,
          },
        };
      });

      return {
        favoritesId: params.id,
        productId: payload.productId,
        product: payload.product,
        operationId: payload.operationId,
        productWasInFavorites: payload.productWasInFavorites,
        previousVersion: previousFavorites?.version,
      } satisfies FavoritesAddProductMutationContext;
    },
    onError: (err, variables, context) => {
      if (context) {
        const currentPending = getFavoritePending(context.productId);

        if (!currentPending || currentPending.operationId === context.operationId) {
          const queryKey = [WebshopFavoritesQueries.GetById, context.favoritesId];
          queryClient.setQueryData<WebshopFavoritesVersioned>(queryKey, old => {
            if (!old || context.productWasInFavorites) {
              return old;
            }

            return {
              ...old,
              version: context.previousVersion ?? old.version,
              favorites: {
                ...old.favorites,
                products: old.favorites.products.filter(
                  product => product.id !== context.productId,
                ),
              },
            };
          });
        }

        // No-op commands ("no changes detected") are harmless. Optimistic-lock
        // conflicts are retried with the real version by the caller; stay silent
        // on the first failure so we only notify if the retry also fails.
        const isSuppressedVersionConflict =
          variables.payload?.suppressVersionConflictToast && isOptimisticLockError(err);
        if (!isNoChangesError(err) && !isSuppressedVersionConflict) {
          toast.error(t?.common.productAddToFavoritesFailed);
        }
      }
      if (context) {
        clearFavoritePending(context.productId, context.operationId);
      }
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context) {
        clearFavoritePending(context.productId, context.operationId);
      }
    },
  });
};

export default useFavoritesAddProductMutate;
